'use client';
import classNames from 'classnames';
import { HamburgerIcon, CrossIcon } from 'components/modulab/Icon';
import { Image } from 'components/modulab/Image';
import { useClient } from 'hooks/useClient';
import { useState, ReactNode, ComponentProps } from 'react';
import { notEmpty } from 'utils/array';
import { None } from 'utils/types';
import styles from './NavBar.module.css';
import { LogoData } from './types';
import { AccountButton } from '$/components/layout/AccountButton';
import { useClientContext } from '$/modules/context/client';

export interface MenuProps {
  children: ReactNode;
  logo: LogoData | None;
  locales: string[];
  accountButton: ComponentProps<typeof AccountButton> | undefined;
}

type LogoProps = { image: LogoData | None };
const Logo = ({ image }: LogoProps) => (
  <div className="grow" data-testid="logo">
    <a
      href={image?.link?.href || '/'}
      title={image?.link?.title}
      className="block h-8 w-40"
    >
      <Image
        layout="fill"
        alt={image?.title || ''}
        src={image?.image?.url}
        width={180}
        objectPosition="left"
        loading="eager"
      />
    </a>
  </div>
);

type LanguageSwitchProps = {
  locales: string[];
};

function LanguageSwitch({ locales }: LanguageSwitchProps) {
  const { langPrefix } = useClientContext();
  const pathname = useClient(
    () => location.pathname.slice(langPrefix.length) + location.search,
  );
  return (
    <div className="flex justify-end py-4 lg1:ml-2 lg1:border-l lg1:border-l-neutral-secondary lg1:py-0 lg1:pl-2">
      {locales.map((prefix) => {
        const activeLanguage = langPrefix.includes(prefix);

        return (
          <span key={prefix} data-selector="nav-bar-language" className="px-2">
            <a
              className={classNames(
                'cursor-pointer text-nowrap font-small',
                activeLanguage
                  ? 'font-bold text-text-primary'
                  : 'text-neutral-tertiary',
              )}
              href={`/${prefix}${pathname}`}
              data-selector="nav-bar-language-link"
            >
              {prefix === 'zh' ? '繁中' : 'EN'}
            </a>
          </span>
        );
      })}
    </div>
  );
}

export function Menu({
  children,
  logo,
  locales,
  accountButton,
}: MenuProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  return (
    <div
      className={classNames(
        'relative z-40 flex items-center gap-gap/x-sm border-b border-b-neutral-secondary p-4 bg-text-secondary font-sans',
        'sm:h-16 lg1:h-24 lg1:px-8 lg1:py-6',
      )}
    >
      <div className="flex-none lg1:hidden">
        <button
          type="button"
          className="mr-2 cursor-pointer text-secondary-darker"
          data-selector-type="mobile-menu-icon"
          onClick={() => setIsMenuOpen(true)}
        >
          <HamburgerIcon size={3} />
        </button>
      </div>
      <div className="grow">
        <Logo image={logo} />
      </div>
      <div className="min-w-0 shrink">
        <div
          className={classNames(
            isMenuOpen ? styles.overlap : '',
            isMenuOpen ? styles.overlapActive : '',
          )}
          style={
            isMenuOpen ? { backgroundColor: 'rgba(0, 0, 0, 0.5)' } : undefined
          }
          data-selector-type="navbar-menu-overlap"
        >
          <ul
            className="absolute left-0 top-0 hidden h-screen w-70 gap-4 overflow-y-auto bg-text-secondary lg1:relative lg1:flex lg1:h-auto lg1:w-full lg1:flex-row lg1:items-center lg1:justify-between lg1:overflow-visible lg1:px-4"
            data-selector-type="nav-menu"
          >
            <div className="flex items-center justify-between border-b border-b-neutral-secondary p-4 lg1:hidden">
              <Logo image={logo} />
              <button
                className="cursor-pointer appearance-none bg-transparent text-text-tint focus:outline-none"
                type="button"
                onClick={() => setIsMenuOpen(false)}
                data-selector-type="nav-bar-mobile-menu-close"
              >
                <CrossIcon size={4} />
              </button>
            </div>

            {children}
            {notEmpty(locales) && <LanguageSwitch locales={locales} />}
          </ul>
        </div>
      </div>
      {accountButton && (
        <div className="z-0 bg-white">
          <AccountButton {...accountButton} />
        </div>
      )}
    </div>
  );
}
